import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'
import { Provider } from 'react-redux'
import {
  createBrowserRouter,
  createMemoryRouter,
  RouterProvider,
} from 'react-router'

import { sec } from '~/shell/api/rtk/security'
import { routes } from '~/shell/app/routes'
import { shouldBypassAuthForE2E } from '~/shell/config'
import { store } from '~/shell/redux/store'

/**
 * The app's data router.
 *
 * Exported because `Auth0ProviderWithConfig`'s `onRedirectCallback` navigates it after
 * login: a data router does not observe the `history.replaceState` auth0-react's default
 * callback performs, so returning the user to their pre-login URL has to go through the
 * router itself.
 */
export const router = createBrowserRouter(routes)

/**
 * Registers the Auth0 token getter with the RTK Query security bridge (AUTH-07).
 *
 * Lives inside the React tree so it has access to `useAuth0`, and runs once so
 * `sec.getAccessTokenSilently` resolves real tokens for outgoing requests before
 * any authenticated query is issued. Also registers the re-auth handler that the
 * base query and the 401 middleware call when the cached session is dead: it
 * kicks off Auth0's interactive login, capturing the current relative URL into
 * `appState.returnTo`. Renders nothing.
 */
const AuthTokenBridge = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()

  useEffect(() => {
    // Under the env-gated E2E bypass there is no real Auth0 session to bridge,
    // so leave the security handlers unset; the base query skips the token fetch.
    if (shouldBypassAuthForE2E) {
      return
    }
    sec.setAccessTokenSilentlyHandler(getAccessTokenSilently)
    sec.setReauthHandler(() => {
      void loginWithRedirect({
        appState: {
          returnTo:
            window.location.pathname +
            window.location.search +
            window.location.hash,
        },
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
      })
    })
  }, [getAccessTokenSilently, loginWithRedirect])

  return null
}

export const App = () => (
  <Provider store={store}>
    <AuthTokenBridge />
    <div id="app">
      <RouterProvider router={router} />
    </div>
  </Provider>
)

/**
 * Test-friendly version of the App backed by a memory router.
 *
 * Use this in tests instead of `App` so routing can be exercised without a real
 * browser. Mirrors outpatient's `TestApp`: it wraps the same `routes` in a
 * `createMemoryRouter` seeded with `initialEntries` and the redux `Provider`.
 */
export const TestApp = ({
  initialEntries = ['/'],
}: {
  initialEntries?: string[]
}) => {
  const testRouter = createMemoryRouter(routes, { initialEntries })
  return (
    <Provider store={store}>
      <div id="app">
        <RouterProvider router={testRouter} />
      </div>
    </Provider>
  )
}
